import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import {
  AdminServerTableCellContext,
  AdminServerTableColumn,
  AdminServerTablePaginationState,
} from '../../../shared/table/models/admin-server-table.model';
import { VentasCementoStore, CementoVentaVm } from '../../../stores/ventas-cemento.store';
import { VentasCementoUiEffectsService } from '../ui-effects/ventas-cemento-ui-effects.service';
import { buildVentasCementoColumns } from './ventas-cemento-tabla.component.columns';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ventas-cemento-tabla',
  templateUrl: './ventas-cemento-tabla.component.html',
  styleUrls: ['./ventas-cemento-tabla.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VentasCementoTablaComponent implements OnInit {
  @ViewChild('priceCellTpl', { static: true })
  priceCellTpl!: TemplateRef<AdminServerTableCellContext<CementoVentaVm>>;
  @ViewChild('actionsCellTpl', { static: true })
  actionsCellTpl!: TemplateRef<AdminServerTableCellContext<CementoVentaVm>>;

  readonly store = inject(VentasCementoStore);
  private readonly uiEffects = inject(VentasCementoUiEffectsService);

  readonly tableColumns: AdminServerTableColumn<CementoVentaVm>[] = [];

  ngOnInit(): void {
    this.tableColumns.push(
      ...buildVentasCementoColumns({
        priceCellTemplate: this.priceCellTpl,
        actionsCellTemplate: this.actionsCellTpl,
      }),
    );
  }

  get paginationState(): AdminServerTablePaginationState {
    return {
      page: this.store.page(),
      pageSize: this.store.pageSize(),
      total: this.store.totalItems(),
      totalPages: this.store.totalPages(),
      hasPreviousPage: this.store.hasPreviousPage(),
      hasNextPage: this.store.hasNextPage(),
      pageStartIndex: this.store.pageStartIndex(),
      pageEndIndex: this.store.pageEndIndex(),
      pageSizeOptions: [10, 20, 50, 100],
    };
  }

  readonly trackByItem = (_: number, item: CementoVentaVm): string => item.trackId;

  onSearchTermChange(value: string | number): void {
    this.store.setSearchTerm(String(value ?? ''));
  }

  onOrderingChange(value: string): void {
    this.store.setOrdering(value);
  }

  onPageChange(value: number): void {
    this.store.setPage(value);
  }

  onPageSizeChange(value: number): void {
    this.store.setPageSize(value);
  }

  onExportExcel(): void {
    this.store.exportExcel().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Ventas_Cemento.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.uiEffects.showExcelSuccess();
      },
      error: () => {
        this.uiEffects.showExcelError();
      },
    });
  }

  onSeeVariables(row: CementoVentaVm): void {
    const html = this.buildVentaDetailHtml(row);
    Swal.fire({
      title: `Detalle venta ${this.escapeHtml(row.order_buy)}`,
      html,
      width: 'min(640px, 94vw)',
      icon: 'info',
      confirmButtonText: 'Cerrar',
    });
  }

  private buildVentaDetailHtml(row: CementoVentaVm): string {
    const d = (v: string | null | undefined): string => this.formatDisplay(v);
    const money = (n: number): string => this.formatMoneyCl(n);
    const section = (title: string, body: string): string =>
      `<div class="mb-3 text-start"><h6 class="border-bottom pb-1 mb-2">${this.escapeHtml(title)}</h6>${body}</div>`;
    const line = (label: string, value: string): string =>
      `<p class="mb-1 small"><strong>${this.escapeHtml(label)}:</strong> ${value}</p>`;

    const transaccion = [
      line('Código orden', d(row.order_buy)),
      line('Número transacción', d(row.order_buy)),
      line('Fecha de creación (solicitud)', d(row.created_at_formatted)),
      line('Fecha de pago', d(row.date_buy_formatted)),
      line('Estado', d(row.state_display)),
      line('Código autorización (CRC)', d(row.authorization_code)),
      line('Código respuesta pasarela', d(row.response_code)),
      line('Método de pago', d(row.payment_method)),
      line('Tipo de pago', d(row.payment_type)),
      line('Monto neto', `$ ${money(row.receipt_total_neto)}`),
      line('IVA', `$ ${money(row.receipt_iva)}`),
      line('Monto total (pagado)', `$ ${money(row.total_price)}`),
    ].join('');

    const cliente = [
      line('RUT cliente', d(row.rut)),
      line('Nombre cliente', d(row.name)),
      line('Nombre de obra', d(row.giro)),
      line('Dirección (facturación)', d(row.address_payment)),
      line('Correo', d(row.email)),
      line('Teléfono', d(row.phone)),
    ].join('');

    const entrega = [
      line('Dirección de entrega', d(row.address_dispatch)),
      line('Latitud', d(String(row.address_latitud ?? ''))),
      line('Longitud', d(String(row.address_longitud ?? ''))),
    ].join('');

    const pallets = Number.isFinite(row.request_amount)
      ? new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(row.request_amount)
      : d(String(row.request_amount));

    const producto = [
      line('Producto', d(row.product_name)),
      line('Código SAP', d(row.product_sku_pos10)),
      line('Cantidad', `${pallets} pallets`),
      line('Tipo de entrega (incoterm)', d(row.incoterm_display)),
      line('Fecha entrega prevista', d(row.schedule_date_formatted)),
    ].join('');

    return [
      section('Detalle de la transacción de pago', transaccion),
      section('Datos cliente', cliente),
      section('Datos de entrega', entrega),
      section('Producto y entrega', producto),
    ].join('');
  }

  private escapeHtml(value: string | number | null | undefined): string {
    const s = String(value ?? '');
    return s
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  private formatDisplay(value: string | null | undefined): string {
    const t = String(value ?? '').trim();
    return t ? this.escapeHtml(t) : '—';
  }

  private formatMoneyCl(value: number): string {
    return new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);
  }
}
